import React, {useState, useEffect, useMemo, useRef} from "react";
import Menu from "../../../../components/layout/menu/index";
import '../../../../../src/index.css';
import { useModuleAdherence } from "../../../../hooks/userAdherence/index";
import ModalSuccess from "../../../../components/shared/modals/modalSuccess";
import Calendar from "../../../../components/shared/calendar";
import SelectField from "../../../../components/forms/selectOption";
import SaveButton from "../../../../components/forms/saveButton";
import CancelButton from "../../../../components/forms/cancelButton";
import TimeSlotEditor from "../../../../components/appointment/timeSlotEditor";
import TimePickerModal from "../../../../components/appointment/timePickerModal";
import ModalDelete from "../../../../components/shared/modals/modalDelete";
import {registerSchedule, getSchedules, updateScheduleStatus, updateSchedules, addNewSchedules,
  getSetting, updateSetting
} from "../../../../client/appointment";
import ModalAlertWarning from "../../../../components/shared/modals/modalAlertWarning";
import EditTimeSlotModal from "../../../../components/appointment/editTimeSlotModal";
import { MODALS } from "../../../../config/images";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function ScheduleAdmin() {
  useModuleAdherence(7);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [selectedTime, setSelectedTime] = useState("");
  const handleSelectTime = (time) => {
    setSelectedTime(time);
  }

  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedProviders, setSelectedProviders] = useState(null);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timeSlotToDelete, setTimeSlotToDelete] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [fetchedSchedules, setFetchedSchedules] = useState([]);

  const [hasExistingSchedules, setHasExistingSchedules] = useState(false);

  const [isAddingNewSchedules, setIsAddingNewSchedules] = useState(false);
  const [newTimeSlots, setNewTimeSlots] = useState([]);

  const [isEditingNewTimeSlot, setIsEditingNewTimeSlot] = useState(false);

  const [editingQueueSupplier, setEditingQueueSupplier] = useState(null);
  const [showEditTimeSlotModal, setShowEditTimeSlotModal] = useState(false);
  const [currentEditingSlot, setCurrentEditingSlot] = useState(null);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactEditText, setContactEditText] = useState("");
  const [contactDisplayText, setContactDisplayText] = useState("");
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [isUpdatingContact, setIsUpdatingContact] = useState(false);
  const quillRef = useRef(null);

  const fetchContactNumber = async () => {
    try {
      setIsLoadingContact(true);
      const response = await getSetting(1); // ID 1 para "administrative-number"
      
      if (response.status === 200 && response.data.setting) {
        const rawContact = response.data.setting.key_value || "";
        setContactDisplayText(rawContact);
        setContactEditText(rawContact); 
      }
    } catch (error) {
      console.error("Error al obtener número de contacto:", error);
      setContactDisplayText("");
      setContactEditText("");
    } finally {
      setIsLoadingContact(false);
    }
  };
  
  const handleUpdateContact = async () => {

    const cleanQuillContent = (htmlContent) => {
      if (!htmlContent) return '';
      
      // Convertir a texto plano
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      // Si el texto está vacío, retornar string vacío
      if (!plainText.trim()) {
        return '';
      }
      
      return htmlContent;
    };

    // Luego en handleUpdateContact:
    const cleanedContent = cleanQuillContent(contactEditText);


    try {
      setIsUpdatingContact(true);

    const request = {
      key_value: cleanedContent
    };
      
      const response = await updateSetting(1, request); // ID 1
      
      if (response.status === 200) {
        setContactDisplayText(cleanedContent);
        setContactEditText(cleanedContent);

        setSuccessMessage("Información de contacto actualizada exitosamente");
        setShowSuccessModal(true);
        setShowContactModal(false);
      }
    } catch (error) {
      console.error("Error al actualizar número de contacto:", error);
      
      if (error.response) {
        const { data } = error.response;
        setErrorMessage(data.message || "Error al actualizar el número de contacto");
      } else {
        setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
      }
      setShowErrorModal(true);
    } finally {
      setIsUpdatingContact(false);
    }
  };

  const charCount = contactEditText ? 
    new DOMParser().parseFromString(contactEditText, 'text/html').body.textContent?.length || 0 
    : 0;
  const isNearLimit = charCount > 480;

  const handleContactChange = (content, delta, source, editor) => {
    const plainText = editor.getText();
    const currentCharCount = plainText.length;
    
    if (currentCharCount <= 501) {
    setContactEditText(content);
    } else {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.setContents(quill.clipboard.convert(contactEditText));
      }
    }
  };


  const openContactModal = async () => {
    await fetchContactNumber(); 
    setShowContactModal(true);
  };

  useEffect(() => {
    fetchContactNumber();
  }, []);

  const getCurrentDateForCalendar = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const generateTimeSlots = (frequency) => {

    const frequencyNum = parseInt(frequency);

    const isSaturday = selectedStartDate ? selectedStartDate.getDay() === 6 : false;

    const startHour = 8; 
    const endHour = isSaturday ? 12 : 16; 
    
    const timeSlots = [];
    
    for (let hour = startHour; hour < endHour; hour += frequencyNum) {
      const nextHour = hour + frequencyNum;

      if (isSaturday && nextHour > 12) {
        break; 
      }
      
      if (nextHour <= endHour) {
        const startHourFormatted = hour.toString().padStart(2, '0');
        const nextHourFormatted = nextHour.toString().padStart(2, '0');
        
      timeSlots.push({
        time: `${startHourFormatted}:00 - ${nextHourFormatted}:00`,
        queue_supplier: selectedProviders ? parseInt(selectedProviders) : null,
        isGenerated: true 
      });
      }
    }
    
    return timeSlots;
  };

  useEffect(() => {
    if (selectedFrequency && !hasExistingSchedules) {
      setTimeSlots(generateTimeSlots(selectedFrequency));
    }
  }, [selectedFrequency, selectedStartDate, hasExistingSchedules]);

  const [timeSlots, setTimeSlots] = useState([]);

  const groupSchedulesByDate = (schedules) => {
    const grouped = {};
    
    schedules.forEach(schedule => {
      const date = schedule.date;
      const [year, month, day] = date.split('-').map(Number);
      const jsDate = new Date(year, month - 1, day); 
      
      const timeSlot = `${schedule.start_time.substring(0, 5)} - ${schedule.end_time.substring(0, 5)}`;

      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      const existingSlotIndex = grouped[date].findIndex(slot => slot.time === timeSlot);

      if (existingSlotIndex === -1) {
        grouped[date].push({
          time: timeSlot,
          block_schedule: schedule.block_schedule,
          scheduleId: schedule.id,
          allScheduleIds: [schedule.id],
          queue_supplier: schedule.queue_supplier,
          originalDate: date, 
          jsDate: jsDate 
        });
      } else {
        grouped[date][existingSlotIndex].block_schedule = schedule.block_schedule;
        if (!grouped[date][existingSlotIndex].allScheduleIds.includes(schedule.id)) {
          grouped[date][existingSlotIndex].allScheduleIds.push(schedule.id);
        }
      }
    });
    
    const sortedDates = Object.keys(grouped).sort();
    
    const datesForComparison = sortedDates.filter(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      return dayOfWeek !== 0; 
    });
    
    if (datesForComparison.length <= 1) {
      const validDates = sortedDates.filter(dateStr => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.getDay() !== 0; 
      });
      
      if (validDates.length > 0) {
        const date = validDates[0];

        return {
          combined: {
            dates: sortedDates, 
            timeSlots: grouped[date] || []
          }
        };
      } else {
        return {
          combined: {
            dates: sortedDates,
            timeSlots: []
          }
        };
      }
    }
    
    const allDatesHaveSameSlots = checkIfAllDatesHaveSameSlots(grouped, datesForComparison);
    
    if (allDatesHaveSameSlots) {
      const allHaveSameBlockStatusAndSupplier = checkIfAllHaveSameBlockStatus(grouped, datesForComparison);
      
      if (allHaveSameBlockStatusAndSupplier) {
        const firstValidDate = datesForComparison[0];
        
        const consolidatedSlots = (grouped[firstValidDate] || []).map(slot => {
          const allIds = [];
          let queueSupplierValue = slot.queue_supplier;
          
          sortedDates.forEach(date => {
            const dateSlot = grouped[date]?.find(s => s.time === slot.time);
            if (dateSlot && dateSlot.allScheduleIds) {
              allIds.push(...dateSlot.allScheduleIds);
            }
          });
          
          return {
            ...slot,
            allScheduleIds: [...new Set(allIds)],
            isCombined: true,
            queue_supplier: queueSupplierValue
          };
        });
        
        return {
          combined: {
            dates: sortedDates, 
            timeSlots: consolidatedSlots
          }
        };
      } else {
        return {
          separated: Object.fromEntries(
            sortedDates.map(date => [date, grouped[date] || []])
          )
        };
      }
    } else {
      return {
        separated: Object.fromEntries(
          sortedDates.map(date => [date, grouped[date] || []])
        )
      };
    }
  };

  const checkIfAllHaveSameBlockStatus = (grouped, dates) => {
    if (dates.length <= 1) return true;
    
    const firstDate = dates[0];
    const firstDateSlots = grouped[firstDate] || [];
    
    if (firstDateSlots.length === 0) {
      return dates.every(date => {
        const dateSlots = grouped[date] || [];
        return dateSlots.length === 0;
      });
    }
    
    return dates.every(date => {
      const currentSlots = grouped[date] || [];
      
      if (currentSlots.length !== firstDateSlots.length) return false;
      
      return firstDateSlots.every((firstSlot, index) => {
        if (currentSlots[index].time !== firstSlot.time) return false;
        
        const sameBlockStatus = currentSlots[index].block_schedule === firstSlot.block_schedule;
        const sameQueueSupplier = currentSlots[index].queue_supplier === firstSlot.queue_supplier;
        
        return sameBlockStatus && sameQueueSupplier;
      });
    });
  };

  const checkIfAllDatesHaveSameSlots = (grouped, dates) => {
    if (dates.length <= 1) return true;
    
    const firstDate = dates[0];
    const firstDateSlots = grouped[firstDate] || [];
    
    if (firstDateSlots.length === 0) {
      return dates.every(date => {
        const dateSlots = grouped[date] || [];
        return dateSlots.length === 0;
      });
    }
    
    
    return dates.every(date => {
      const dateSlots = grouped[date] || [];
      
      if (dateSlots.length === 0 && firstDateSlots.length > 0) return false;
      
      if (dateSlots.length !== firstDateSlots.length) return false;
      
      return firstDateSlots.every((firstSlot, index) => {
        const currentSlot = dateSlots[index];
        
        if (currentSlot.time !== firstSlot.time) return false;
        
        return true;
      });
    });
  };

  const fetchSchedules = async () => {
    if (!selectedStartDate) return;
    
    try {
      setIsLoading(true);
      let response;
      
      if (selectedEndDate) {
        const startDateStr = formatDateToYYYYMMDD(selectedStartDate);
        const endDateStr = formatDateToYYYYMMDD(selectedEndDate);
        response = await getSchedules(null, startDateStr, endDateStr);
      } else {
        const dateStr = formatDateToYYYYMMDD(selectedStartDate);
        response = await getSchedules(dateStr, null, null);
      }


      
    if (response.status === 200) {

      if (response.data.schedules && response.data.schedules.length > 0) {
        const schedulesByDate = groupSchedulesByDate(response.data.schedules);
        setFetchedSchedules(schedulesByDate);
        setHasExistingSchedules(true);

        if (timeSlots.length > 0) {
          setTimeSlots([]);
          setSelectedFrequency(null);
        }
        
      } else {

        setFetchedSchedules({
          combined: {
            dates: [],
            timeSlots: []
          }
        });
        setHasExistingSchedules(false);
      }
    }
    } catch (error) {
      console.error("Error al obtener horarios:", error);
      setFetchedSchedules({
        combined: {
          dates: [],
          timeSlots: []
        }
      });
      setHasExistingSchedules(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStartDate) {
      fetchSchedules();
    } else {
      setFetchedSchedules([]);
      setHasExistingSchedules(false);
    }

    setNewTimeSlots([]);
    setSelectedProviders(null);
    setSelectedFrequency(null);
    setTimeSlots([]);
    setEditingQueueSupplier(null);
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (selectedStartDate) {
      fetchSchedules();
    }
  }, []); 
  
  const handleSaveEditTimeSlot = async () => {
    
    if (!startTime || !endTime) {
      setErrorMessage("Por favor, seleccione tanto la hora inicial como la hora final");
      setShowErrorModal(true);
      return;
    }
    
    if (!editingQueueSupplier) {
      setErrorMessage("Por favor, seleccione la cantidad de proveedores");
      setShowErrorModal(true);
      return;
    }
    
    if (editingQueueSupplier < 1 || editingQueueSupplier > 10) {
      setErrorMessage("La cantidad de proveedores debe estar entre 1 y 10");
      setShowErrorModal(true);
      return;
    }

    const startTimeStr = startTime.substring(0, 5);
    const endTimeStr = endTime.substring(0, 5);
    const newTimeSlotString = `${startTimeStr} - ${endTimeStr}`;
    
    const existingValidation = validateTimeSlot(newTimeSlotString);
    if (!existingValidation.valid) {
      setErrorMessage(existingValidation.message);
      setShowErrorModal(true);
      return;
    }
    
    const dateForValidation = currentEditingSlot?.jsDate || selectedStartDate || new Date();
    const editValidation = validateEditTimeSlot(startTimeStr, endTimeStr, dateForValidation);
    if (!editValidation.valid) {
      setErrorMessage(editValidation.message);
      setShowErrorModal(true);
      return;
    }
    
    if (isAddingNewSchedules && !currentEditingSlot) {
      if (!editingQueueSupplier) {
        setErrorMessage("Por favor, seleccione la cantidad de proveedores");
        setShowErrorModal(true);
        return;
      }
      
      const validation = validateTimeSlot(newTimeSlotString);
      if (!validation.valid) {
        setErrorMessage(validation.message);
        setShowErrorModal(true);
        return;
      }
      
      if (hasBackendSchedules) {
        let dates = [];
        if (selectedEndDate) {
          dates = getDatesInRange(selectedStartDate, selectedEndDate);
        } else {
          dates = [selectedStartDate];
        }

        // Obtener horarios existentes
        const existingSchedulesByDate = {};
        if (fetchedSchedules) {
          if (fetchedSchedules.combined && fetchedSchedules.combined.dates.length > 0) {
            fetchedSchedules.combined.dates.forEach(dateStr => {
              existingSchedulesByDate[dateStr] = fetchedSchedules.combined.timeSlots.map(slot => ({
                start: slot.time.split(' - ')[0],
                end: slot.time.split(' - ')[1]
              }));
            });
          } else if (fetchedSchedules.separated) {
            Object.entries(fetchedSchedules.separated).forEach(([dateStr, slots]) => {
              existingSchedulesByDate[dateStr] = slots.map(slot => ({
                start: slot.time.split(' - ')[0],
                end: slot.time.split(' - ')[1]
              }));
            });
          }
        }

        // Validar para cada fecha
        for (const date of dates) {
          const dateStr = formatDateToYYYYMMDD(date);
          const existingSlotsForDate = existingSchedulesByDate[dateStr] || [];
          
          for (const existingSlot of existingSlotsForDate) {
            const isOverlapping = checkTimeOverlap(
              startTimeStr, endTimeStr,
              existingSlot.start, existingSlot.end
            );

            if (isOverlapping) {
              setErrorMessage(
              // `Error en ${formatDateString(dateStr)}: ` +
                `El horario ${startTimeStr} - ${endTimeStr} se cruza con el horario existente ` +
                `${existingSlot.start} - ${existingSlot.end}`
              );
              setShowErrorModal(true);
              return;
            }
          }
        for (const otherSlot of newTimeSlots) {
          const otherSlotTime = typeof otherSlot === 'object' ? otherSlot.time : otherSlot;
          if (otherSlotTime === newTimeSlotString) continue;
          
          const [otherStart, otherEnd] = otherSlotTime.split(' - ');
          
          const isOverlapping = checkTimeOverlap(
            startTimeStr, endTimeStr,
            otherStart, otherEnd
          );

          if (isOverlapping) {
            setErrorMessage(
              `Error en ${formatDateString(dateStr)}: ` +
              `El horario ${startTimeStr} - ${endTimeStr} se cruza con otro horario que está agregando ` +
              `(${otherStart} - ${otherEnd})`
            );
            setShowErrorModal(true);
            return;
          }
        }
        }
        addToNewTimeSlots(newTimeSlotString);
      } else {

      const newSlotObject = {
        time: newTimeSlotString,
        queue_supplier: editingQueueSupplier,
        isLocal: true 
      };
      
      const isOverlapping = timeSlots.some(existingSlot => {
        const existingTime = typeof existingSlot === 'object' ? existingSlot.time : existingSlot;
        const [existingStart, existingEnd] = existingTime.split(' - ');
        
        return checkTimeOverlap(
          startTimeStr, endTimeStr,
          existingStart, existingEnd
        );
      });
      
      if (isOverlapping) {
        setErrorMessage("El horario se solapa con un horario existente");
        setShowErrorModal(true);
        return;
      }
      
      setTimeSlots(prev => {
        const updated = [...prev, newSlotObject];
        return updated.sort((a, b) => {
          const timeA = typeof a === 'object' ? a.time : a;
          const timeB = typeof b === 'object' ? b.time : b;
          const startA = parseInt(timeA.split(' - ')[0].split(':')[0]) * 60 + parseInt(timeA.split(' - ')[0].split(':')[1]);
          const startB = parseInt(timeB.split(' - ')[0].split(':')[0]) * 60 + parseInt(timeB.split(' - ')[0].split(':')[1]);
          return startA - startB;
        });
      });
      
      setSuccessMessage("Horario agregado exitosamente");
      setShowSuccessModal(true);
    }
      
      setShowEditTimeSlotModal(false);
      setIsAddingNewSchedules(false);
      return;
    }
    
    // CASO 2: editando un horario existente
    if (!currentEditingSlot) return;
    
    try {
      setIsSubmitting(true);
      
      if (typeof currentEditingSlot === 'object' && currentEditingSlot.scheduleId) {
        // CASO 2A: horario del backend
        if (!editingQueueSupplier) {
          setErrorMessage("Por favor, seleccione la cantidad de proveedores");
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }

        if (fetchedSchedules) {
          let existingSlots = [];
          
          if (fetchedSchedules.combined && fetchedSchedules.combined.timeSlots) {
            existingSlots = fetchedSchedules.combined.timeSlots;
          } else if (fetchedSchedules.separated) {
            // Obtener todos los horarios de todas las fechas
            Object.values(fetchedSchedules.separated).forEach(slots => {
              existingSlots = [...existingSlots, ...slots];
            });
          }
          
          // Filtrar el horario que se está editando
          const otherSlots = existingSlots.filter(slot => 
            !currentEditingSlot.allScheduleIds?.includes(slot.scheduleId) && 
            slot.scheduleId !== currentEditingSlot.scheduleId
          );
          
          for (const otherSlot of otherSlots) {
            const [otherStart, otherEnd] = otherSlot.time.split(' - ');
            
            const isOverlapping = checkTimeOverlap(
              startTimeStr, endTimeStr,
              otherStart, otherEnd
            );

            if (isOverlapping) {
              setErrorMessage(
                `El horario ${startTimeStr} - ${endTimeStr} se cruza con el horario existente ` +
                `${otherSlot.time}`
              );
              setShowErrorModal(true);
              setIsSubmitting(false);
              return;
            }
          }
        }

        let scheduleIds = [];
        
        if (currentEditingSlot.allScheduleIds && currentEditingSlot.allScheduleIds.length > 0) {
          scheduleIds = currentEditingSlot.allScheduleIds;
        } else {
          scheduleIds = [currentEditingSlot.scheduleId];
        }
        
        if (scheduleIds.length === 0) {
          setErrorMessage("No se encontraron IDs de horarios para actualizar");
          setShowErrorModal(true);
          return;
        }
        
        if (currentEditingSlot.block_schedule === true) {
          setErrorMessage("No se puede editar un horario bloqueado");
          setShowErrorModal(true);
          return;
        }
        
        const request = {
          Schedules: scheduleIds.map(id => ({
            id: id,
            startTime: startTimeStr,
            endTime: endTimeStr,
            queue_supplier: editingQueueSupplier
          }))
        };
        
        
        const response = await updateSchedules(request);
        
        if (response.status === 200) {
          setSuccessMessage(response.data.message || "¡Horarios actualizados exitosamente!");
          setShowSuccessModal(true);
          fetchSchedules();
        }
      } else {
        // CASO 2B: Es un horario local o nuevo

        const isSlotInNewTimeSlots = newTimeSlots.some(slot => {
          if (typeof currentEditingSlot === 'object' && currentEditingSlot.time) {
            if (typeof slot === 'object' && slot.time === currentEditingSlot.time) {
              return true;
            }
            return slot === currentEditingSlot.time;
          } else {
            if (typeof slot === 'object' && slot.time === currentEditingSlot) {
              return true;
            }
            return slot === currentEditingSlot;
          }
        });

        if (isEditingNewTimeSlot || isSlotInNewTimeSlots) {

          if (hasBackendSchedules) {
            let dates = [];
            if (selectedEndDate) {
              dates = getDatesInRange(selectedStartDate, selectedEndDate);
            } else {
              dates = [selectedStartDate];
            }

            const existingSchedulesByDate = {};
            if (fetchedSchedules) {
              if (fetchedSchedules.combined && fetchedSchedules.combined.dates.length > 0) {
                fetchedSchedules.combined.dates.forEach(dateStr => {
                  existingSchedulesByDate[dateStr] = fetchedSchedules.combined.timeSlots.map(slot => ({
                    start: slot.time.split(' - ')[0],
                    end: slot.time.split(' - ')[1]
                  }));
                });
              } else if (fetchedSchedules.separated) {
                Object.entries(fetchedSchedules.separated).forEach(([dateStr, slots]) => {
                  existingSchedulesByDate[dateStr] = slots.map(slot => ({
                    start: slot.time.split(' - ')[0],
                    end: slot.time.split(' - ')[1]
                  }));
                });
              }
            }

            for (const date of dates) {
              const dateStr = formatDateToYYYYMMDD(date);
              const existingSlotsForDate = existingSchedulesByDate[dateStr] || [];
              
              for (const existingSlot of existingSlotsForDate) {
                const isOverlapping = checkTimeOverlap(
                  startTimeStr, endTimeStr,
                  existingSlot.start, existingSlot.end
                );

                if (isOverlapping) {
                  setErrorMessage(
                    `Error en ${formatDateString(dateStr)}: ` +
                    `El horario editado ${startTimeStr} - ${endTimeStr} se cruza con el horario existente ` +
                    `${existingSlot.start} - ${existingSlot.end}`
                  );
                  setShowErrorModal(true);
                  setIsSubmitting(false);
                  return;
                }
              }
            }
          }

        const otherNewSlots = newTimeSlots.filter(slot => {
          const slotTime = typeof slot === 'object' ? slot.time : slot;
          const currentTime = typeof currentEditingSlot === 'object' ? currentEditingSlot.time : currentEditingSlot;
          return slotTime !== currentTime;
        });

          for (const otherSlot of otherNewSlots) {
            const otherSlotTime = typeof otherSlot === 'object' ? otherSlot.time : otherSlot;
            const [otherStart, otherEnd] = otherSlotTime.split(' - ');
            
            const isOverlapping = checkTimeOverlap(
              startTimeStr, endTimeStr,
              otherStart, otherEnd
            );

            if (isOverlapping) {
              setErrorMessage(
                `El horario editado ${startTimeStr} - ${endTimeStr} se cruza con otro horario ` +
                `que está agregando (${otherStart} - ${otherEnd})`
              );
              setShowErrorModal(true);
              setIsSubmitting(false);
              return;
            }
          }

          const updatedNewTimeSlots = newTimeSlots.map(slot => {
            const slotTime = typeof slot === 'object' ? slot.time : slot;
            const currentTime = typeof currentEditingSlot === 'object' ? currentEditingSlot.time : currentEditingSlot;
            
            if (slotTime === currentTime) {
              if (typeof slot === 'object') {
                return {
                  ...slot,
                  time: newTimeSlotString,
                  queue_supplier: editingQueueSupplier
                };
              } else {
                return {
                  time: newTimeSlotString,
                  queue_supplier: editingQueueSupplier,
                  isNewSlot: true
                };
              }
            }
            return slot;
          });
          setNewTimeSlots(updatedNewTimeSlots);
          
          setSuccessMessage("Horario editado exitosamente en la lista de horarios a agregar");
          setShowSuccessModal(true);
        } else {

          const otherLocalSlots = timeSlots.filter(slot => {
            const slotTime = typeof slot === 'object' ? slot.time : slot;
            const currentTime = typeof currentEditingSlot === 'object' ? currentEditingSlot.time : currentEditingSlot;
            return slotTime !== currentTime;
          });

          for (const otherSlot of otherLocalSlots) {
            const otherTime = typeof otherSlot === 'object' ? otherSlot.time : otherSlot;
            const [otherStart, otherEnd] = otherTime.split(' - ');
            
            const isOverlapping = checkTimeOverlap(
              startTimeStr, endTimeStr,
              otherStart, otherEnd
            );

            if (isOverlapping) {
              setErrorMessage(
                `El horario editado ${startTimeStr} - ${endTimeStr} se cruza con el horario ` +
                `${otherStart} - ${otherEnd}`
              );
              setShowErrorModal(true);
              setIsSubmitting(false);
              return;
            }
          }

          const updatedTimeSlots = timeSlots.map(slot => {
            const slotTime = typeof slot === 'object' ? slot.time : slot;
            const currentTime = typeof currentEditingSlot === 'object' ? currentEditingSlot.time : currentEditingSlot;
            
            if (slotTime === currentTime) {
              if (typeof slot === 'object') {
                return {
                  ...slot,
                  time: newTimeSlotString,
                  queue_supplier: editingQueueSupplier
                };
              } else {
                return {
                  time: newTimeSlotString,
                  queue_supplier: editingQueueSupplier,
                  isLocal: true
                };
              }
            }
            return slot;
          });
          
          setTimeSlots(updatedTimeSlots);
          
          setSuccessMessage("Horario editado exitosamente");
          setShowSuccessModal(true);
        }
      }
      
    } catch (error) {
      console.error("Error al actualizar horario:", error);
      
      if (error.response) {
        const { data } = error.response;
        
        if (data.errors && Array.isArray(data.errors)) {
          const errorDetails = data.errors.join('\n');
          setErrorMessage(`${data.message || "Errores en los horarios"}\n\n${errorDetails}`);
        } else {
          setErrorMessage(data.message || "Error al actualizar el horario");
        }
      } else {
        setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
      }
      
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
      setShowEditTimeSlotModal(false);
      setCurrentEditingSlot(null);
      setIsEditingNewTimeSlot(false);
      setIsAddingNewSchedules(false);
      setEditingQueueSupplier(null);
    }
  };

  const validateTimeSlot = (timeSlot) => {
    const [start, end] = timeSlot.split(' - ');
    const startHour = parseInt(start.split(':')[0]);
    const startMinute = parseInt(start.split(':')[1]);
    const endHour = parseInt(end.split(':')[0]);
    const endMinute = parseInt(end.split(':')[1]);
    
    if (startHour < 8) {
      return { valid: false, message: "La hora inicial no puede ser antes de las 8:00" };
    }
    
    if (endHour > 18 || (endHour === 18 && endMinute > 0)) {
      return { valid: false, message: "La hora final no puede ser después de las 18:00" };
    }
    
    if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
      return { valid: false, message: "La hora final debe ser mayor que la hora inicial" };
    }
    
    // Validación para sábados
    const validationDate = selectedStartDate || new Date();
    const dayOfWeek = validationDate.getDay();
    
    if (dayOfWeek === 6) { 
      if (endHour > 12 || (endHour === 12 && endMinute > 0)) {
        return { valid: false, message: "Los sábados la hora final no puede ser después de las 12:00" };
      }
    }
    
    return { valid: true };
  };

  const validateEditTimeSlot = (startTimeStr, endTimeStr, date) => {
    const startHour = parseInt(startTimeStr.split(':')[0]);
    const startMinute = parseInt(startTimeStr.split(':')[1]);
    const endHour = parseInt(endTimeStr.split(':')[0]);
    const endMinute = parseInt(endTimeStr.split(':')[1]);
    
    if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
      return { 
        valid: false, 
        message: "La hora final debe ser mayor que la hora inicial" 
      };
    }
    
    const dayOfWeek = date ? date.getDay() : (selectedStartDate ? selectedStartDate.getDay() : new Date().getDay());
    
    //  sábados
    if (dayOfWeek === 6) { 
      if (startHour < 8 || endHour > 12 || (endHour === 12 && endMinute > 0)) {
        return { 
          valid: false, 
          message: "Los sábados solo se permiten horarios de 8:00 a 12:00" 
        };
      }
    } else {
      // días de semana
      if (startHour < 8 || endHour > 18 || (endHour === 18 && endMinute > 0)) {
        return { 
          valid: false, 
          message: "Los horarios deben estar entre 8:00 y 18:00" 
        };
      }
    }
    
    return { valid: true };
  };

  const handleFrequencyChange = (e) => {
    if (hasExistingSchedules) {
      return;
    }
    
    const frequency = e.target.value;
    setSelectedFrequency(frequency);
    
    if (frequency) {
      setTimeSlots(generateTimeSlots(frequency));
    }
  };

  const handleDateSelect = (dateInfo) => {
    setSelectedProviders(null);
    setSelectedFrequency(null);
    setTimeSlots([]);
    setNewTimeSlots([]);
    setEditingQueueSupplier(null);

    const isCurrentDay = isCurrentDate(dateInfo.startDate);
    if (isCurrentDay) {
      dateInfo.endDate = null;
    }

    setSelectedStartDate(dateInfo.startDate);
    setSelectedEndDate(dateInfo.endDate);
  };

  const getDisplayTimeSlots = () => {
    if (!fetchedSchedules || Object.keys(fetchedSchedules).length === 0) {
      return timeSlots;
    }
    
    return fetchedSchedules;
  };
  

  const formatDisplayDate = () => {
    if (!selectedStartDate) {
      return "Seleccione una fecha";
    }
    
    const formatDate = (date) => {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    if (fetchedSchedules && fetchedSchedules.combined && fetchedSchedules.combined.dates.length > 0) {
      if (fetchedSchedules.combined.dates.length === 1) {
        return formatDateString(fetchedSchedules.combined.dates[0]);
      } else {
        const firstDate = fetchedSchedules.combined.dates[0];
        const lastDate = fetchedSchedules.combined.dates[fetchedSchedules.combined.dates.length - 1];
        return `${formatDateString(firstDate)} - ${formatDateString(lastDate)}`;
      }
    }
    
    if (fetchedSchedules && fetchedSchedules.separated && Object.keys(fetchedSchedules.separated).length > 0) {
      const dates = Object.keys(fetchedSchedules.separated).sort();
      if (dates.length > 0) {
        return formatDateString(dates[0]);
      }
    }
    
    if (selectedEndDate) {
      return `${formatDate(selectedStartDate)} - ${formatDate(selectedEndDate)}`;
    } else {
      return formatDate(selectedStartDate);
    }
  };

  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeToHHMM = (timeStr) => {
    if (timeStr.includes(' - ')) {
      const [start, end] = timeStr.split(' - ');
      return { start: start, end: end };
    }
    return { start: timeStr, end: timeStr };
  };

  const isDateValidForScheduling = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    
    if (dateCopy < today) {
      return false;
    }
    
    if (dateCopy.getDay() === 0) {
      return false;
    }
    
    return true;
  };

  const isCurrentDate = (date) => {
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dateCopy = new Date(date);
    dateCopy.setHours(0, 0, 0, 0);
    
    return dateCopy.getTime() === today.getTime();
  };

  const validateTimeSlotForDay = (timeSlot, date) => {
    const [start, end] = timeSlot.split(' - ');
    const startHour = parseInt(start.split(':')[0]);
    const endHour = parseInt(end.split(':')[0]);
    const endMinute = parseInt(end.split(':')[1]);
    
    const dayOfWeek = date.getDay(); 
    
    if (dayOfWeek === 6) {
      if (startHour < 8 || endHour > 12 || (endHour === 12 && endMinute > 0)) {
        return {
          valid: false,
          message: "Los sábados solo se permiten horarios de 8:00 a 12:00"
        };
      }
    } else {
      if (startHour < 8 || endHour > 18 || (endHour === 18 && endMinute > 0)) {
        return {
          valid: false,
          message: "Los horarios deben estar entre 8:00 y 18:00"
        };
      }
    }
    
    return { valid: true };
  };
  
  const handleSaveSchedules = async () => {
    // CASO 1: Si hay horarios del backend Y hay nuevos horarios para agregar
    if (hasBackendSchedules && newTimeSlots.length > 0) {
      await saveNewSchedulesToExistingDates();
      return;
    }

    // CASO 2: Si hay horarios del backend pero NO hay nuevos para agregar
    if (hasBackendSchedules && newTimeSlots.length === 0) {
      setErrorMessage("Ya existen horarios para esta fecha. Use 'Agregar horarios' para añadir más.");
      setShowErrorModal(true);
      return;
    }

    // CASO 3: Si NO hay horarios del backend (crear nuevos horarios)
    if (!hasBackendSchedules) {
      if (timeSlots.length === 0) {
        setErrorMessage("No hay horarios configurados");
        setShowErrorModal(true);
        return;
      }
      
      await registerNewSchedules();
      return;
    }
  };
  
const saveNewSchedulesToExistingDates = async () => {
  try {
    setIsSubmitting(true);

    if (!selectedStartDate) {
      setErrorMessage("Por favor, seleccione una fecha");
      setShowErrorModal(true);
      return;
    }

    if (newTimeSlots.length === 0) {
      setErrorMessage("No hay horarios para agregar");
      setShowErrorModal(true);
      return;
    }

    const slotsWithoutProviders = newTimeSlots.filter(slot => {
      const queueSupplierValue = typeof slot === 'object' ? slot.queue_supplier : null;
      return !queueSupplierValue || queueSupplierValue < 1 || queueSupplierValue > 10;
    });

    if (slotsWithoutProviders.length > 0) {
      setErrorMessage("Algunos horarios no tienen cantidad de proveedores asignada o tienen un valor inválido (debe ser entre 1 y 10)");
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    let dates = [];
    if (selectedEndDate) {
      dates = getDatesInRange(selectedStartDate, selectedEndDate);
    } else {
      dates = [selectedStartDate];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const validDates = dates.filter(date => {
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);
      return dateCopy >= today && dateCopy.getDay() !== 0;
    });

    if (validDates.length === 0) {
      setErrorMessage("No hay fechas válidas para agregar horarios");
      setShowErrorModal(true);
      return;
    }

    // Obtener horarios existentes para todas las fechas
    const existingSchedulesByDate = {};
    
    if (fetchedSchedules) {
      if (fetchedSchedules.combined && fetchedSchedules.combined.dates.length > 0) {
        // Si están combinados, todos los horarios aplican a todas las fechas
        fetchedSchedules.combined.dates.forEach(dateStr => {
          existingSchedulesByDate[dateStr] = fetchedSchedules.combined.timeSlots.map(slot => ({
            start: slot.time.split(' - ')[0],
            end: slot.time.split(' - ')[1]
          }));
        });
      } else if (fetchedSchedules.separated) {
        // Si están separados, cada fecha tiene sus propios horarios
        Object.entries(fetchedSchedules.separated).forEach(([dateStr, slots]) => {
          existingSchedulesByDate[dateStr] = slots.map(slot => ({
            start: slot.time.split(' - ')[0],
            end: slot.time.split(' - ')[1]
          }));
        });
      }
    }

    // Validar cada nuevo horario contra los existentes en cada fecha
    for (const date of validDates) {
      const dateStr = formatDateToYYYYMMDD(date);
      const existingSlotsForDate = existingSchedulesByDate[dateStr] || [];
      
      for (const timeSlot of newTimeSlots) {
        const timeSlotStr = typeof timeSlot === 'object' ? timeSlot.time : timeSlot;
        
        if (!timeSlotStr || typeof timeSlotStr !== 'string') {
          setErrorMessage(`Error en ${dateStr}: Formato de horario inválido`);
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }
        
        // Validación de límites de horario
        const validation = validateTimeSlotForDay(timeSlotStr, date);
        if (!validation.valid) {
          setErrorMessage(`Error en ${dateStr}: ${validation.message}`);
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }

        // Validación de cruce con horarios existentes
        const [newStart, newEnd] = timeSlotStr.split(' - ');
        
        for (const existingSlot of existingSlotsForDate) {
          const isOverlapping = checkTimeOverlap(
            newStart, newEnd,
            existingSlot.start, existingSlot.end
          );

          if (isOverlapping) {
            setErrorMessage(
             // `Error en la fecha ${formatDateString(dateStr)}: ` +
              `El horario ${newStart} - ${newEnd} se cruza con el horario existente ` +
              `${existingSlot.start} - ${existingSlot.end}`
            );
            setShowErrorModal(true);
            setIsSubmitting(false);
            return;
          }
        }

        // También validar contra otros nuevos horarios en la misma fecha
        const otherNewSlots = newTimeSlots.filter(s => 
          (typeof s === 'object' ? s.time : s) !== timeSlotStr
        );

        for (const otherSlot of otherNewSlots) {
          const otherSlotStr = typeof otherSlot === 'object' ? otherSlot.time : otherSlot;
          const [otherStart, otherEnd] = otherSlotStr.split(' - ');
          
          const isOverlapping = checkTimeOverlap(
            newStart, newEnd,
            otherStart, otherEnd
          );

          if (isOverlapping) {
            setErrorMessage(
             // `Error en ${formatDateString(dateStr)}: ` +
              `Los horarios que intenta agregar (${newStart} - ${newEnd}) y ` +
              `(${otherStart} - ${otherEnd}) se cruzan entre sí`
            );
            setShowErrorModal(true);
            setIsSubmitting(false);
            return;
          }
        }
      }
    }

    const schedules = [];
    
    validDates.forEach(date => {
      const dateStr = formatDateToYYYYMMDD(date);
      
      newTimeSlots.forEach(slot => {
        const timeSlotStr = typeof slot === 'object' ? slot.time : slot;
        const queueSupplierValue = typeof slot === 'object' ? slot.queue_supplier : null;
        
        if (!queueSupplierValue || queueSupplierValue < 1 || queueSupplierValue > 10) {
          setErrorMessage(`El horario "${timeSlotStr}" no tiene una cantidad válida de proveedores (debe ser entre 1 y 10)`);
          setIsSubmitting(false);
          return;
        }

        if (!timeSlotStr || typeof timeSlotStr !== 'string' || !timeSlotStr.includes(' - ')) {
          setErrorMessage(`Formato de horario inválido: "${timeSlotStr}"`);
          setIsSubmitting(false);
          return;
        }
        
        const [startTime, endTime] = timeSlotStr.split(' - ');
        
        schedules.push({
          date: dateStr,
          start_time: startTime,
          end_time: endTime,
          queue_supplier: queueSupplierValue
        });
      });
    });

    const request = {
      Schedules: schedules
    };


    const response = await addNewSchedules(request);
    
    if (response.status === 200 || response.status === 201) {
      setSuccessMessage(response.data.message || `¡${schedules.length} horario(s) agregado(s) exitosamente!`);
      setShowSuccessModal(true);
      
      setNewTimeSlots([]);
      await fetchSchedules();

      setIsAddingNewSchedules(false);
      setEditingQueueSupplier(null);
    }
    
  } catch (error) {
    console.error("Error al agregar nuevos horarios:", error);
    
    if (error.response) {
      const { data } = error.response;
      
      if (data.errors && Array.isArray(data.errors)) {
        setErrorMessage(`${data.message}`);
        setShowErrorModal(true);
      } else {
        setErrorMessage(data.message || "Error al agregar los horarios");
        setShowErrorModal(true);
      }
    } else {
      setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
      setShowErrorModal(true);
    }
  } finally {
    setIsSubmitting(false);
  }
};

// Función auxiliar para verificar cruce de horarios
const checkTimeOverlap = (start1, end1, start2, end2) => {
  const toMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  const start1Minutes = toMinutes(start1);
  const end1Minutes = toMinutes(end1);
  const start2Minutes = toMinutes(start2);
  const end2Minutes = toMinutes(end2);

  return (
    (start1Minutes >= start2Minutes && start1Minutes < end2Minutes) ||
    (end1Minutes > start2Minutes && end1Minutes <= end2Minutes) ||
    (start1Minutes <= start2Minutes && end1Minutes >= end2Minutes)
  );
};

  const registerNewSchedules = async () => {
    if (!selectedStartDate) {
      setErrorMessage("Por favor, seleccione una fecha");
      setShowErrorModal(true);
      return;
    }

    const slotsWithoutProviders = timeSlots.filter(slot => {
      if (typeof slot === 'object') {
        return !slot.queue_supplier || slot.queue_supplier < 1 || slot.queue_supplier > 10;
      }
      return true;
    });

    if (slotsWithoutProviders.length > 0) {
      setErrorMessage("Algunos horarios no tienen cantidad de proveedores asignada o tienen un valor inválido (debe ser entre 1 y 10)");
      setShowErrorModal(true);
      return;
    }

    const displaySlots = getDisplayTimeSlots();
    
    if (displaySlots.length === 0) {
      setErrorMessage("No hay horarios configurados");
      setShowErrorModal(true);
      return;
    }

    let dates = [];
    if (selectedEndDate) {
      dates = getDatesInRange(selectedStartDate, selectedEndDate);
    } else {
      dates = [selectedStartDate];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const validDates = dates.filter(date => {
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);
      return dateCopy > today && dateCopy.getDay() !== 0; 
    });

    if (validDates.length === 0) {
      setErrorMessage("No hay fechas válidas para registrar. No se permiten fechas de hoy, pasadas ni domingos.");
      setShowErrorModal(true);
      return;
    }

    for (const date of validDates) {
      for (const timeSlot of timeSlots) {
        const timeSlotStr = typeof timeSlot === 'object' ? timeSlot.time : timeSlot;
        const validation = validateTimeSlotForDay(timeSlotStr, date);
        if (!validation.valid) {
          const dateStr = formatDateToYYYYMMDD(date);
          setErrorMessage(`Error en ${dateStr}: ${validation.message}`);
          setShowErrorModal(true);
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);
      
      const schedules = [];
      
      validDates.forEach(date => {
        timeSlots.forEach(timeSlot => {
          const timeSlotStr = typeof timeSlot === 'object' ? timeSlot.time : timeSlot;
          const queueSupplierValue = typeof timeSlot === 'object' && timeSlot.queue_supplier 
            ? timeSlot.queue_supplier 
            : parseInt(selectedProviders);
  
          const times = formatTimeToHHMM(timeSlotStr);
  
          const scheduleItem = {
            date: formatDateToYYYYMMDD(date),
            start_time: times.start,
            end_time: times.end,
            queue_supplier: queueSupplierValue
          };
  
          schedules.push(scheduleItem);
        });
      });

      const request = {
        Schedules: schedules,
      };


      const response = await registerSchedule(request);
      
      if (response.status === 200 || response.status === 201) {
        setSuccessMessage(response.data.message || "¡Horarios creados exitosamente!");
        setShowSuccessModal(true);
        

        setSelectedProviders(null);
        setSelectedFrequency(null);
        setTimeSlots([]);

        await fetchSchedules();

        setEditingQueueSupplier(null);
      }
      
    } catch (error) {
      console.error("Error al crear horarios:", error);
      
      if (error.response) {
        const { data } = error.response;
        
        if (data.errors && Array.isArray(data.errors)) {
          setErrors(data.errors);
          setErrorMessage(data.message || "Errores en los datos de horarios");
          setShowErrorModal(true);
        } else {
          setErrorMessage(data.message || "Error al crear los horarios");
          setShowErrorModal(true);
        }
      } else {
        setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
        setShowErrorModal(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlockTimeSlot = async (slot) => {
    if (typeof slot === 'string' || !slot.scheduleId) {
      setSuccessMessage(`¡Sus horarios fueron bloqueados con éxito!`);
      setShowSuccessModal(true);
      return;
    }

    try {
      setIsSubmitting(true);
      
      let scheduleIds = [];
      
      if (slot.allScheduleIds && slot.allScheduleIds.length > 0) {
        scheduleIds = slot.allScheduleIds;
      } else if (slot.scheduleId) {
        scheduleIds = [slot.scheduleId];
      }

      if (scheduleIds.length === 0 && fetchedSchedules && fetchedSchedules.separated) {
        const allIds = [];
        Object.values(fetchedSchedules.separated).forEach(dateSlots => {
          dateSlots.forEach(dateSlot => {
            if (dateSlot.time === slot.time && dateSlot.scheduleId) {
              allIds.push(dateSlot.scheduleId);
            }
          });
        });
        scheduleIds = allIds;
      }
      
      const action = slot.block_schedule === true ? 'unblock' : 'block';
      const request = {
        scheduleIds: scheduleIds,
        action: action
      };
      
      
      const response = await updateScheduleStatus(request);
      
      if (response.status === 200) {
        const message = action === 'block' 
          ? "¡Su horario fue bloqueado con éxito!" 
          : "¡Su horario fue desbloqueado con éxito!";
        
        setSuccessMessage(message);
        setShowSuccessModal(true);
        
        fetchSchedules();
      }
      
    } catch (error) {
      console.error(`Error al ${slot.block_schedule === true ? 'desbloquear' : 'bloquear'} horario:`, error);
      
      if (error.response) {
        const { data } = error.response;
        
        if (data.scheduleIdsWithAppointments) {
          setErrorMessage("No se puede bloquear este horario porque tiene citas agendadas");
        } else {
          setErrorMessage(data.message || `Error al ${slot.block_schedule === true ? 'desbloquear' : 'bloquear'} el horario`);
        }
      } else {
        setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
      }
      
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTimeSlot = (slot) => {
    if (typeof slot === 'object' && slot.scheduleId) {
      setTimeSlotToDelete(slot);
      setShowDeleteModal(true);
    } else {
      setTimeSlotToDelete(typeof slot === 'object' ? slot.time : slot);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!timeSlotToDelete) {
      setShowDeleteModal(false);
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (typeof timeSlotToDelete === 'object' && timeSlotToDelete.scheduleId) {
        let scheduleIds = [];
        
        if (timeSlotToDelete.allScheduleIds && timeSlotToDelete.allScheduleIds.length > 0) {
          scheduleIds = timeSlotToDelete.allScheduleIds;
        } else {
          scheduleIds = [timeSlotToDelete.scheduleId];
        }
        
        const request = {
          scheduleIds: scheduleIds,
          action: 'softdelete'
        };
        
        
        const response = await updateScheduleStatus(request);
        
        if (response.status === 200) {
          setSuccessMessage("¡Horario eliminado exitosamente!");
          setShowSuccessModal(true);
          
          fetchSchedules();
        }
      } else {
        const timeSlotText = typeof timeSlotToDelete === 'object' ? timeSlotToDelete.time : timeSlotToDelete;
        const updatedTimeSlots = timeSlots.filter(slot => {
          if (typeof slot === 'object') {
            return slot.time !== timeSlotText;
          }
          return slot !== timeSlotText;
        });
        setTimeSlots(updatedTimeSlots);
        
        setSuccessMessage(`¡Su horario fue eliminado con éxito!`);
        setShowSuccessModal(true);
      }
      
    } catch (error) {
      console.error("Error al eliminar horario:", error);
      
      if (error.response) {
        const { data } = error.response;
        setErrorMessage(data.message || "Error al eliminar el horario");
      } else {
        setErrorMessage("Error de conexión. Por favor, intente nuevamente.");
      }
      
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
      setShowDeleteModal(false);
      setTimeSlotToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTimeSlotToDelete(null);
  };

  const handleCancel = () => {
  
    // CASO 1: Si hay horarios del backend Y hay nuevos horarios para agregar
    if (hasBackendSchedules && newTimeSlots.length > 0) {
      setNewTimeSlots([]);
      return;
    }
    
    // CASO 2: Si NO hay horarios del backend (creando nuevos horarios desde cero)
    if (!hasBackendSchedules) {

      setSelectedProviders(null);
      setSelectedFrequency(null);
      setTimeSlots([]);
      setFetchedSchedules([]);
      setHasExistingSchedules(false);
      setEditingQueueSupplier(null);
      setNewTimeSlots([]);
      
      return;
    }
    
    // CASO 3: Si hay horarios del backend pero NO hay nuevos para agregar
    if (hasBackendSchedules && newTimeSlots.length === 0) {
      setErrorMessage("No hay cambios pendientes para cancelar");
      setShowErrorModal(true);
    }
  };

  const handleEditTimeSlot = (slot) => {
    setCurrentEditingSlot(slot);
    
    const isNewSlot = newTimeSlots.some(newSlot => 
      typeof newSlot === 'object' ? 
      newSlot.time === (typeof slot === 'object' ? slot.time : slot) : 
      newSlot === slot
    );

    setIsEditingNewTimeSlot(isNewSlot);
    
    let startTimeStr, endTimeStr, queueSupplierValue;
    
    if (typeof slot === 'object') {
      // Caso 1: Es un objeto (puede ser de backend, local o newTimeSlots)
      const slotTime = slot.time || (typeof slot === 'string' ? slot : null);
      
      if (slotTime && slotTime.includes(' - ')) {
        const times = slotTime.split(' - ');
        startTimeStr = times[0] + ':00';
        endTimeStr = times[1] + ':00';
      }
      
      if (slot.queue_supplier !== undefined && slot.queue_supplier !== null) {
        queueSupplierValue = slot.queue_supplier;
      } 
      else if (slot.isNewSlot) {
        queueSupplierValue = slot.queue_supplier || editingQueueSupplier;
      }
      else if (slot.isGenerated && selectedProviders) {
        queueSupplierValue = parseInt(selectedProviders);
      }
      else if (slot.isLocal && slot.queue_supplier !== undefined) {
        queueSupplierValue = slot.queue_supplier;
      }
      else if (slot.scheduleId && slot.queue_supplier !== undefined) {
        queueSupplierValue = slot.queue_supplier;
      }
      else {
        queueSupplierValue = selectedProviders ? parseInt(selectedProviders) : null;
      }
      
    } else if (typeof slot === 'string') {
      // Caso 2: Es un string (puede ser de newTimeSlots antiguo o local)
      if (slot.includes(' - ')) {
        const [start, end] = slot.split(' - ');
        startTimeStr = start + ':00';
        endTimeStr = end + ':00';
      }
      
      const foundNewSlot = newTimeSlots.find(newSlot => 
        typeof newSlot === 'object' ? newSlot.time === slot : newSlot === slot
      );
      
      if (foundNewSlot) {
        if (typeof foundNewSlot === 'object' && foundNewSlot.queue_supplier !== undefined) {
          queueSupplierValue = foundNewSlot.queue_supplier;
        } else {
          queueSupplierValue = editingQueueSupplier || (selectedProviders ? parseInt(selectedProviders) : null);
        }
      } else {
        const foundLocalSlot = timeSlots.find(s => {
          if (typeof s === 'object' && s.time === slot) {
            return true;
          }
          return s === slot;
        });
        
        if (typeof foundLocalSlot === 'object' && foundLocalSlot.queue_supplier !== undefined) {
          queueSupplierValue = foundLocalSlot.queue_supplier;
        } else {
          queueSupplierValue = selectedProviders ? parseInt(selectedProviders) : null;
        }
      }
    }
    
    setStartTime(startTimeStr);
    setEndTime(endTimeStr);
    setEditingQueueSupplier(queueSupplierValue);
    setShowEditTimeSlotModal(true);
  };

  const handleProvidersSelect = (e) => {
    if (hasExistingSchedules) {
      return;
    }
    
    const providers = e.target.value;
    setSelectedProviders(providers);
    
    if (timeSlots.length > 0) {
      const updatedTimeSlots = timeSlots.map(slot => ({
        ...slot,
        queue_supplier: providers ? parseInt(providers) : null
      }));
      setTimeSlots(updatedTimeSlots);
    }
  };

  useEffect(() => {
    if (timeSlots.length > 0 && selectedProviders && !hasExistingSchedules) {
      const updatedTimeSlots = timeSlots.map(slot => {
        if (slot.isGenerated) {
          return {
            ...slot,
            queue_supplier: parseInt(selectedProviders)
          };
        }
        return slot;
      });
      
      setTimeSlots(updatedTimeSlots);
    }
  }, [selectedProviders, hasExistingSchedules]);

useEffect(() => {
  const handleScheduleUpdate = (event) => {
    const { scheduleId, usedSlots, remainingSlots } = event.detail;
    
    if (fetchedSchedules) {
      if (fetchedSchedules.combined && fetchedSchedules.combined.timeSlots) {
        const updatedTimeSlots = fetchedSchedules.combined.timeSlots.map(slot => {
          if (slot.scheduleId === scheduleId) {
            return {
              ...slot,
              queue_supplier: remainingSlots
            };
          }
          if (slot.allScheduleIds?.includes(scheduleId)) {
            return {
              ...slot,
              queue_supplier: remainingSlots
            };
          }
          return slot;
        });
        
        setFetchedSchedules({
          ...fetchedSchedules,
          combined: {
            ...fetchedSchedules.combined,
            timeSlots: updatedTimeSlots
          }
        });
      }
      
      if (fetchedSchedules.separated) {
        const updatedSeparated = {};
        Object.entries(fetchedSchedules.separated).forEach(([date, slots]) => {
          updatedSeparated[date] = slots.map(slot => {
            if (slot.scheduleId === scheduleId) {
              return {
                ...slot,
                queue_supplier: remainingSlots
              };
            }
            if (slot.allScheduleIds?.includes(scheduleId)) {
              return {
                ...slot,
                queue_supplier: remainingSlots
              };
            }
            return slot;
          });
        });
        
        setFetchedSchedules({
          ...fetchedSchedules,
          separated: updatedSeparated
        });
      }
    }
  };

  window.addEventListener('schedule-updated', handleScheduleUpdate);
  
  return () => {
    window.removeEventListener('schedule-updated', handleScheduleUpdate);
  };
}, [fetchedSchedules]);

  const handleAddTimeSlot = () => {
    if (selectedStartDate && !isDateValidForScheduling(selectedStartDate)) {
      setErrorMessage("No se pueden crear horarios para esta fecha. No se permiten fechas de hoy, pasadas ni domingos.");
      setShowErrorModal(true);
      return;
    }

    setStartTime(null);
    setEndTime(null);
    

    let initialQueueSupplier = null;
    
    if (!hasBackendSchedules && selectedProviders) {
      initialQueueSupplier = parseInt(selectedProviders);
    }

    setEditingQueueSupplier(initialQueueSupplier);

    setIsAddingNewSchedules(true);
    setCurrentEditingSlot(null);
    setIsEditingNewTimeSlot(false);

    setShowEditTimeSlotModal(true);
  };

  const shouldShowTimeSlotEditor = () => {
    if (!selectedStartDate) return false;
    
    let hasValidFetchedSchedules = false;
    
    if (fetchedSchedules) {
      if (fetchedSchedules.combined) {
        if (fetchedSchedules.combined.timeSlots && 
            Array.isArray(fetchedSchedules.combined.timeSlots) && 
            fetchedSchedules.combined.timeSlots.length > 0) {
          hasValidFetchedSchedules = true;
        }
      } else if (fetchedSchedules.separated) {
        const dates = Object.keys(fetchedSchedules.separated);
        if (dates.length > 0) {
          const hasSlots = dates.some(date => {
            const slots = fetchedSchedules.separated[date];
            return slots && Array.isArray(slots) && slots.length > 0;
          });
          hasValidFetchedSchedules = hasSlots;
        }
      }
    }
    
    const hasLocalTimeSlots = timeSlots.length > 0;
    
    const hasFrequencySelected = !!selectedFrequency;
    
    const hasNewTimeSlots = newTimeSlots.length > 0;

    return hasValidFetchedSchedules || hasLocalTimeSlots || hasFrequencySelected || hasNewTimeSlots;
  };

  const formatDateString = (dateStr) => {
    if (dateStr && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  const hasBackendSchedules = useMemo(() => {
    if (!fetchedSchedules) return false;
    
    if (fetchedSchedules.combined?.timeSlots?.length > 0) {
      return true;
    }
    
    if (fetchedSchedules.separated) {
      const dates = Object.keys(fetchedSchedules.separated);
      return dates.some(date => {
        const slots = fetchedSchedules.separated[date];
        return slots && Array.isArray(slots) && slots.length > 0;
      });
    }
    
    return false;
  }, [fetchedSchedules]);

  const isSaveButtonEnabled = useMemo(() => {
    
    if (!selectedStartDate) return false;

    if (isCurrentDate(selectedStartDate)) {
      return false;
    }

    if (!isDateValidForScheduling(selectedStartDate)) {
      return false;
    }

    if (hasBackendSchedules) {
      return newTimeSlots.length > 0 && !isSubmitting && !isLoading;
    }
    
    if (timeSlots.length === 0) return false;

    const allSlotsHaveProviders = timeSlots.every(slot => {
      if (typeof slot === 'string') {
        return selectedProviders !== null && selectedProviders !== undefined;
      } else if (typeof slot === 'object') {
        return slot.queue_supplier !== undefined && slot.queue_supplier !== null;
      }
      return false;
    });
    
    if (!allSlotsHaveProviders) return false;

      if (isSubmitting || isLoading) return false;
    
    return true;
  }, [selectedStartDate, selectedProviders, timeSlots, hasBackendSchedules, newTimeSlots, isSubmitting, isLoading]);

  const isCancelButtonEnabled = useMemo(() => {
    // Caso A: Hay horarios del backend Y nuevos horarios pendientes
    if (hasBackendSchedules && newTimeSlots.length > 0) {
      return true;
    }
    
    // Caso B: NO hay horarios del backend Y tiene horarios locales
    if (!hasBackendSchedules && (timeSlots.length > 0  || selectedProviders || selectedFrequency)) {
      return true;
    }
    
    return false;
  }, [hasBackendSchedules, newTimeSlots, timeSlots, selectedStartDate, selectedProviders, selectedFrequency]);

  const addToNewTimeSlots = (timeSlot) => {
    const existingIndex = newTimeSlots.findIndex(slot => {
      const slotTime = typeof slot === 'object' ? slot.time : slot;
      return slotTime === timeSlot;
    });
    
    if (existingIndex !== -1) {
      const updatedNewTimeSlots = [...newTimeSlots];
      
      if (typeof updatedNewTimeSlots[existingIndex] === 'object') {
        updatedNewTimeSlots[existingIndex] = {
          ...updatedNewTimeSlots[existingIndex],
          queue_supplier: editingQueueSupplier
        };
      } else {
        updatedNewTimeSlots[existingIndex] = {
          time: timeSlot,
          queue_supplier: editingQueueSupplier,
          isNewSlot: true
        };
      }
      
      setNewTimeSlots(updatedNewTimeSlots);
      setSuccessMessage(`Horario "${timeSlot}" actualizado en la lista.`);
    } else {
      const newSlotObject = {
        time: timeSlot,
        queue_supplier: editingQueueSupplier,
        isNewSlot: true
      };

      setNewTimeSlots(prev => {
        const updated = [...prev, newSlotObject];
        return updated.sort((a, b) => {
          const timeA = typeof a === 'object' ? a.time : a;
          const timeB = typeof b === 'object' ? b.time : b;
          const startA = parseInt(timeA.split(' - ')[0].split(':')[0]) * 60 + parseInt(timeA.split(' - ')[0].split(':')[1]);
          const startB = parseInt(timeB.split(' - ')[0].split(':')[0]) * 60 + parseInt(timeB.split(' - ')[0].split(':')[1]);
          return startA - startB;
        });
      });

      setSuccessMessage(`Horario "${timeSlot}" agregado a la lista. Se agregará a ${selectedEndDate ? 'todas las fechas del rango' : 'la fecha seleccionada'} cuando haga clic en Guardar.`);
    }
    
    setShowSuccessModal(true);
  };

  const isSundaySelected = useMemo(() => {
    if (!selectedStartDate) return false;
    
    const dayOfWeek = selectedStartDate.getDay();
    return dayOfWeek === 0; 
  }, [selectedStartDate]);



  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300"/>
      <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
        <div className="sm:pt-8 flex flex-col gap-5 pt-4 pb-10 px-[4%] sm:px-[3%]">
            <h1 className="text-[#184A7D] font-itcbold md:text-[32px] text-2xl">
              Gestión de horarios
            </h1>

            <div className="flex flex-col gap-3">
              <h2 className="text-[18px] break-words text-blue font-itcdemi">Contacto de TASA:</h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-full p-2 border rounded-md border-gray">
                  {isLoadingContact ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 border-b-2 rounded-full animate-spin border-blue"></div>
                      <p className="text-sm text-gray font-itcbook">Cargando...</p>
                    </div>
                  ) : contactDisplayText ? (
                    <div 
                      className="w-full p-2 overflow-hidden text-base break-words whitespace-normal link font-itcbook text-blue-dark"
                      dangerouslySetInnerHTML={{
                        __html: (contactDisplayText || "-").split(/\s+/).slice(0, 300).join(' ')
                      }}
                      style={{ 
                        color: 'black',
                        hyphens: 'auto',
                        WebkitHyphens: 'auto',
                        msHyphens: 'auto'
                      }}
                    />
                  ) : (
                    <p className="text-base break-words text-gray font-itcbook">
                      Escriba información
                    </p>
                  )}
                </div>
                <button 
                  onClick={openContactModal}
                  disabled={isLoadingContact}
                >
                  <svg className="cursor-pointer" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 18.9998H6.425L16.2 9.2248L14.775 7.7998L5 17.5748V18.9998ZM3 20.9998V16.7498L17.625 2.1748L21.8 6.4498L7.25 20.9998H3ZM15.475 8.5248L14.775 7.7998L16.2 9.2248L15.475 8.5248Z" 
                      fill={isLoadingContact ? "#D1D5DB" : "#007696"} />
                  </svg>
                </button>
              </div>
            </div>

            <h2 className="text-[#6B7280] text-xl md:text-2xl font-itcmedium">
                Horarios disponibles
            </h2>

            <div className="flex flex-col md:flex-row">
              <div className="flex flex-col w-full md:w-1/2">
                <h3 className="text-left text-white px-4 p-2 font-itcbold bg-blue md:text-[20px] text-base ">
                  Calendario
                </h3>
                <div className="flex flex-col md:h-[682px] pb-8 gap-6 bg-[#0076961A] p-4 md:px-6 justify-center ">
                    <Calendar 
                      initialDate={getCurrentDateForCalendar()}
                      onDateSelect={handleDateSelect}
                    />
                  <div className="flex flex-col items-start gap-2 ">
                    <p className="text-lg text-blue font-itcdemi">Frecuencia de horas: <span className="text-red-500">*</span></p>
                    <SelectField
                      name="frequency"
                      value={selectedFrequency}
                      onChange={handleFrequencyChange}
                      width="w-[208px]"
                      options={[
                        { value: "1", label: "Cada 1 hora" },
                        { value: "2", label: "Cada 2 horas" },
                        { value: "3", label: "Cada 3 horas" }
                      ]}
                      placeholder="Seleccione frecuencia"
                      disabled={hasExistingSchedules || isSubmitting || isLoading || !selectedStartDate || isSundaySelected || isCurrentDate(selectedStartDate)}
                    />
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <p className="text-lg text-blue font-itcdemi">Cantidad de proveedores: <span className="text-red-500">*</span></p>
                    <SelectField
                      name="providers"
                      value={selectedProviders}
                      onChange={handleProvidersSelect}
                      width="w-[208px]"
                      placeholder="Escoja número"
                      options={[
                        { value: "1", label: "1 proveedor" },
                        { value: "2", label: "2 proveedores" },
                        { value: "3", label: "3 proveedores" },
                        { value: "4", label: "4 proveedores" },
                        { value: "5", label: "5 proveedores" },
                        { value: "6", label: "6 proveedores" },
                        { value: "7", label: "7 proveedores" },
                        { value: "8", label: "8 proveedores" },
                        { value: "9", label: "9 proveedores" },
                        { value: "10", label: "10 proveedores" }
                      ]}
                      disabled={hasExistingSchedules || isSubmitting || isLoading || !selectedStartDate || isSundaySelected || isCurrentDate(selectedStartDate)}
                    />
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <SaveButton
                      onClick={handleSaveSchedules}
                      title={hasBackendSchedules && newTimeSlots.length > 0 ? 
                        `Guardar (${newTimeSlots.length} nuevo(s))` : 
                        "Guardar"
                      }
                      height="h-[54px] sm:h-[64px]"
                      width="w-[250px]"
                      disabled={!isSaveButtonEnabled || isSubmitting || isLoading}
                      loading={isSubmitting}
                    />
                    <CancelButton 
                      height="h-[54px] sm:h-[64px]"
                      width="w-[250px]"
                      onClick={handleCancel} 
                      disabled={!isCancelButtonEnabled || isSubmitting || isLoading}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-full md:w-1/2">
                <h3 className="text-left  text-white px-4 p-2 font-itcbold bg-blue  md:text-[20px] text-base">
                  Horarios
                </h3>
                <div className="flex md:overflow-hidden md:overflow-y-auto md:max-h-[682px] flex-col gap-4 p-4">
                  <div className={`flex ${ newTimeSlots.length > 0 ? 'justify-end items-end' : 'justify-between  items-center'} `}>
                    {!newTimeSlots.length > 0 &&  (
                      <p className="text-xl font-itcdemi text-blue">
                        {formatDisplayDate()}
                      </p>
                    )}
                    <button 
                      onClick={handleAddTimeSlot}
                      disabled={isLoading || !selectedStartDate || !isDateValidForScheduling(selectedStartDate) || isCurrentDate(selectedStartDate)}
                      className={`flex items-center gap-2 p-2 border rounded-lg ${!isLoading && selectedStartDate && isDateValidForScheduling(selectedStartDate) && !isCurrentDate(selectedStartDate) ? 'border-green hover:bg-green/10' : 'border-gray-300 cursor-not-allowed'}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.03904 10.0667C2.63904 9.85998 1.65904 8.62665 1.65904 5.92665L1.65904 5.83998C1.65904 2.85998 2.85237 1.66665 5.83237 1.66665L10.1724 1.66665C13.1524 1.66665 14.3457 2.85998 14.3457 5.83998L14.3457 5.92665C14.3457 8.60665 13.379 9.83998 11.019 10.06" 
                        stroke={!isLoading && selectedStartDate && isDateValidForScheduling(selectedStartDate) && !isCurrentDate(selectedStartDate) ? "#76A140" : "#D1D5DB"}  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 14.6667L8 6.08008"
                        stroke={!isLoading && selectedStartDate && isDateValidForScheduling(selectedStartDate) && !isCurrentDate(selectedStartDate) ? "#76A140" : "#D1D5DB"}  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M5.76575 7.56641L7.99909 5.33307L10.2324 7.56641"
                        stroke={!isLoading && selectedStartDate && isDateValidForScheduling(selectedStartDate) && !isCurrentDate(selectedStartDate) ? "#76A140" : "#D1D5DB"}  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <p className="text-xs text-gray font-itcmedium">
                        {hasBackendSchedules ? "Agregar más horarios" : "Agregar horarios"}
                      </p>
                    </button>
                  </div>

                  {isLoading && (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-blue"></div>
                      <span className="ml-2 text-gray-600">Cargando horarios...</span>
                    </div>
                  )}

                  {!isLoading && selectedStartDate && !shouldShowTimeSlotEditor() && (
                    <div className="flex flex-col items-center justify-center py-8 ">
                    {(!selectedEndDate || selectedStartDate.getTime() === selectedEndDate?.getTime()) && isSundaySelected ? ( 
                      <>
                      <p className="mb-2 text-gray-500 font-itcmedium">Día no laborable</p>
                      <p className="text-sm text-center text-gray-400 font-itcbook">
                        La generación de horarios no está disponible.
                      </p>
                      </>    
                    ) : (
                      <>
                      <p className="mb-2 text-gray-500 font-itcmedium">No hay horarios configurados para esta fecha</p>
                      <p className="text-sm text-center text-gray-400 font-itcbook">
                        Selecciona una frecuencia para generar horarios automáticamente<br />
                        o haz clic en "Agregar horarios" para crearlos manualmente
                      </p>
                      </>
                    )}
                    </div>
                  )}

                  {newTimeSlots.length > 0 && (
                    <div className="mb-4">
                      <h4 className="mb-2 text-lg font-itcdemi text-green">Nuevos horarios a agregar:</h4>
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                        <TimeSlotEditor
                          timeSlots={newTimeSlots}
                          selectedTime={selectedTime}
                          onTimeSelect={handleSelectTime}
                          onEdit={handleEditTimeSlot}
                          onDelete={(slot) => {
                            const updatedSlots = newTimeSlots.filter(s => 
                              typeof s === 'object' ? s.time !== (typeof slot === 'object' ? slot.time : slot) : s !== slot
                            );
                            setNewTimeSlots(updatedSlots);
                          }}
                          onBlock={() => {
                            setErrorMessage("Debe guardar los horarios primero para poder bloquearlos");
                            setShowErrorModal(true);
                          }}
                          buttonClasses="bg-yellow-500 hover:bg-yellow-600"
                          hideBlockButton={true} 
                        />
                      </div>
                    </div>
                  )}
                  {!isLoading && shouldShowTimeSlotEditor() && (
                    <div className="flex flex-col gap-4">
                      {fetchedSchedules && fetchedSchedules.combined && fetchedSchedules.combined.timeSlots && fetchedSchedules.combined.timeSlots.length > 0 && (
                        <div className="mb-4">
                          {newTimeSlots.length > 0 && (
                            <p className="mb-2 text-xl font-itcdemi text-blue">
                              {formatDisplayDate()}
                            </p>
                          )}
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <TimeSlotEditor
                              timeSlots={fetchedSchedules.combined.timeSlots}
                              selectedTime={selectedTime}
                              onTimeSelect={handleSelectTime}
                              onEdit={handleEditTimeSlot}
                              onDelete={handleDeleteTimeSlot}
                              onBlock={handleBlockTimeSlot}
                              isDisabled={isCurrentDate(selectedStartDate)}
                            />
                          </div>
                        </div>
                      )}

                      {fetchedSchedules && fetchedSchedules.separated && (
                        <>
                        {Object.entries(fetchedSchedules.separated)
                          .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                          .map(([date, slots]) => (
                          <div key={date} className="mb-4">
                            {date !== Object.keys(fetchedSchedules.separated).sort()[0] && newTimeSlots.length === 0 ? (
                              <p className="mb-2 text-xl font-itcdemi text-blue">
                                {formatDateString(date)}
                              </p>
                            ) : date !== Object.keys(fetchedSchedules.separated).sort() && newTimeSlots.length > 0 ? (
                              <p className="mb-2 text-xl font-itcdemi text-blue">
                                {formatDateString(date)}
                              </p>
                            ) : null}
                              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                <TimeSlotEditor
                                  timeSlots={slots}
                                  selectedTime={selectedTime}
                                  onTimeSelect={handleSelectTime}
                                  onEdit={handleEditTimeSlot}
                                  onDelete={handleDeleteTimeSlot}
                                  onBlock={handleBlockTimeSlot}
                                  isDisabled={isCurrentDate(selectedStartDate)}
                                />
                              </div>
                          </div>
                          ))
                        }
                        </>
                      )}
                            
                      {/* Si hay horarios generados localmente (por frecuencia o manuales) */}
                      {(timeSlots.length > 0 || selectedFrequency) && (
                        <div className="mb-4">
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <TimeSlotEditor
                              timeSlots={timeSlots}
                              selectedTime={selectedTime}
                              onTimeSelect={handleSelectTime}
                              onEdit={handleEditTimeSlot}
                              onDelete={handleDeleteTimeSlot}
                              onBlock={() => {
                                setErrorMessage("Debe guardar los horarios primero para poder bloquearlos");
                                setShowErrorModal(true);
                              }}                                
                              isDisabled={isCurrentDate(selectedStartDate)}
                              hideBlockButton={true}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>

      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="lg:w-[35%] md:w-[45%] w-[90%] h-auto bg-white rounded-3xl shadow-lg flex flex-col pb-5  ">
            <div className='flex justify-end'>
              <button
                onClick={() => setShowContactModal(false)}
                className="px-4 py-2 rounded-md"
                disabled={isUpdatingContact}
              >
                <img src={MODALS.CLOSE} alt="cerrar"/>
              </button>
            </div>
            <div className='flex flex-col items-center justify-center gap-3 px-10'>
              <h3 className="text-sm md:text-lg font-itcdemi">Editar información de contacto</h3>
              <style>
                {`
                .ql-container {
                  border-bottom-left-radius: 10px !important;
                  border-bottom-right-radius: 10px !important;
                  background-color: white;
                  height: 145px !important;
                  width: 100%;
                }
                .ql-toolbar {
                  border-top-right-radius: 10px !important;
                  border-top-left-radius: 10px !important;
                  background-color: white;
                }
                .ql-editor {
                  font-size: 16px !important; 
                }
                `}
              </style>
              <ReactQuill
                ref={quillRef}
                value={contactEditText}
                onChange={handleContactChange}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                  ]
                }}
                formats={[
                  'bold', 
                  'italic', 
                  'underline',
                ]}
                className="w-full overflow-visible text-base outline-none "
                style={{  overflow: 'visible'}} 
              />

              <div className={`text-xs text-right font-itcbook ${isNearLimit ? 'text-red' : 'text-gray'}`}>
                {charCount}/500 caracteres
              </div>
            </div>
            <button
              onClick={handleUpdateContact}
              disabled={isUpdatingContact}
              className="pt-4 text-sm underline md:text-lg text-blue font-itcbook"
            >
              Actualizar
            </button>
          </div>
        </div>
      )}

      <EditTimeSlotModal
        isOpen={showEditTimeSlotModal}
        onClose={() => {
          setShowEditTimeSlotModal(false);
          setCurrentEditingSlot(null);
          setIsAddingNewSchedules(false);
          setIsEditingNewTimeSlot(false);
        }}
        startTime={startTime}
        endTime={endTime}
        onStartTimeClick={() => setShowStartTimePicker(true)}
        onEndTimeClick={() => setShowEndTimePicker(true)}
        queueSupplier={editingQueueSupplier}
        onQueueSupplierChange={(value) => {
          setEditingQueueSupplier(value);
        }}
        onSave={handleSaveEditTimeSlot}
        isSubmitting={isSubmitting}
        title={isAddingNewSchedules ? "Agregar nuevo horario" : "Editar horario"}
      />

      {/* modo edicion */}
      <TimePickerModal
        isOpen={showStartTimePicker}
        onClose={() => setShowStartTimePicker(false)}
        initialTime={startTime}
        title="Hora inicial"
        onSave={(time) => {
          setStartTime(time);
          setShowStartTimePicker(false);
        }}
      />

      {/* TimePicker para hora final en edición */}
      <TimePickerModal
        isOpen={showEndTimePicker}
        onClose={() => setShowEndTimePicker(false)}
        initialTime={endTime}
        title="Hora final"
        onSave={(time) => {
          setEndTime(time);
          setShowEndTimePicker(false);
        }}
      />

      <ModalDelete
        showModalDelete={showDeleteModal}
        closeModal={handleCancelDelete}
        accept={handleConfirmDelete}
        textType="horario"
        pronoun="el"
      />

      <ModalSuccess
        showModalSuccess={showSuccessModal}
        closeModalSuccess={() => {
          setShowSuccessModal(false);
        }}
        fill="#76A140"
        text={successMessage}
      />

      <ModalAlertWarning
        showModalAlertWarning={showErrorModal}
        closeModal={() => {
          setShowErrorModal(false);
          setErrorMessage("");
          setErrors([]);
        }}
        accept={() => {
          setShowErrorModal(false);
          setErrorMessage("");
          setErrors([]);
        }}
        error={errorMessage}
      />
    </div>
  );
}